<template>
  <div class="container-fluid">
    <div class="block-header">
      <bread-crumb :breadCrumbIteams="breadCrumbIteams"></bread-crumb>
    </div>

    <!-- Overview header -->
    <div class="row clearfix">
      <div class="col-12">
        <div class="card card-top-line">
          <div class="header d-flex justify-content-between align-items-center">
            <template v-if="overviewLoading">
              <div class="skel skel-line" style="width: 220px; height: 1.4rem"></div>
              <div class="skel skel-pill" style="width: 80px; height: 1.5rem"></div>
            </template>
            <template v-else>
              <h2 v-if="business" class="mb-0">
                {{ business.businessname }}
              </h2>
              <h2 v-else class="mb-0">Business Financials</h2>
              <span v-if="business" class="badge" :class="businessStatusClass">
                {{ businessStatusLabel }}
              </span>
            </template>
          </div>
          <div class="body pt-2">
            <h6 class="mb-2">Wallet Balances</h6>

            <div v-if="overviewLoading" class="row">
              <div
                v-for="n in 3"
                :key="`wallet-skel-${n}`"
                class="col-lg-4 col-md-6 mb-3"
              >
                <div class="card mb-0 border">
                  <div class="body">
                    <div class="skel skel-line mb-3" style="width: 60px; height: 1.4rem"></div>
                    <div class="d-flex justify-content-between">
                      <div style="width: 30%">
                        <div class="skel skel-line skel-sm mb-2" style="width: 70%"></div>
                        <div class="skel skel-line" style="width: 90%; height: 0.95rem"></div>
                      </div>
                      <div style="width: 30%">
                        <div class="skel skel-line skel-sm mb-2" style="width: 65%"></div>
                        <div class="skel skel-line" style="width: 85%; height: 0.95rem"></div>
                      </div>
                      <div style="width: 30%">
                        <div class="skel skel-line skel-sm mb-2" style="width: 55%"></div>
                        <div class="skel skel-line" style="width: 80%; height: 0.95rem"></div>
                      </div>
                    </div>
                    <div class="skel skel-line skel-sm mt-3" style="width: 50%"></div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="overviewError" class="alert alert-danger mb-0">
              <i class="fa fa-exclamation-circle mr-1"></i> {{ overviewError }}
              <b-button variant="outline-danger" size="sm" class="ml-3" @click="loadOverview">
                Retry
              </b-button>
            </div>

            <template v-else>
              <div v-if="!balances.length" class="text-muted py-2">
                No wallets found for this business.
              </div>
              <div v-else class="row">
                <div
                  v-for="wallet in balances"
                  :key="wallet.walletId"
                  class="col-lg-4 col-md-6 mb-3"
                >
                  <div class="card mb-0 border">
                    <div class="body">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <h5 class="mb-0">{{ wallet.currency }}</h5>
                      </div>
                      <div class="d-flex justify-content-between">
                        <div>
                          <small class="text-muted d-block">Available</small>
                          <strong class="text-success">
                            {{ formatAmount(wallet.available) }}
                          </strong>
                        </div>
                        <div>
                          <small class="text-muted d-block">Pending</small>
                          <strong class="text-warning">
                            {{ formatAmount(wallet.pending) }}
                          </strong>
                        </div>
                        <div>
                          <small class="text-muted d-block">Hold</small>
                          <strong>{{ formatAmount(wallet.hold) }}</strong>
                        </div>
                      </div>
                      <small class="text-muted d-block mt-2">
                        Last updated: {{ formatDateTime(wallet.lastUpdated) }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Transactions list -->
    <div class="row clearfix">
      <div class="col-12">
        <div class="card card-top-line">
          <div class="header d-flex justify-content-between align-items-center">
            <h2>Transactions</h2>
            <div class="d-flex">
              <b-button
                variant="outline-primary"
                size="sm"
                class="mr-2"
                :disabled="!!exporting"
                @click="exportTransactions('csv')"
              >
                <b-spinner
                  v-if="exporting === 'csv'"
                  small
                  class="mr-1"
                ></b-spinner>
                <i v-else class="fa fa-file-text-o mr-1"></i>
                {{ exporting === "csv" ? "Exporting…" : "CSV" }}
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                :disabled="!!exporting"
                @click="exportTransactions('pdf')"
              >
                <b-spinner
                  v-if="exporting === 'pdf'"
                  small
                  class="mr-1"
                ></b-spinner>
                <i v-else class="fa fa-file-pdf-o mr-1"></i>
                {{ exporting === "pdf" ? "Exporting…" : "PDF" }}
              </b-button>
            </div>
          </div>
          <div class="body pt-0">
            <!-- Filters -->
            <div class="business-financials-filters py-3">
              <b-form-row>
                <b-col md="6" lg="3" class="mb-2">
                  <label class="small text-muted mb-1">From</label>
                  <b-form-input
                    type="date"
                    size="sm"
                    v-model="filters.date_from"
                  ></b-form-input>
                </b-col>
                <b-col md="6" lg="3" class="mb-2">
                  <label class="small text-muted mb-1">To</label>
                  <b-form-input
                    type="date"
                    size="sm"
                    v-model="filters.date_to"
                  ></b-form-input>
                </b-col>
                <b-col md="6" lg="3" class="mb-2 status-filter">
                  <label class="small text-muted mb-1">Status</label>
                  <div class="position-relative" v-click-outside="closeStatus">
                    <button
                      type="button"
                      class="status-filter-toggle form-control form-control-sm d-flex justify-content-between align-items-center"
                      :class="{ open: statusOpen }"
                      @click="statusOpen = !statusOpen"
                    >
                      <span class="text-truncate">{{ statusButtonLabel }}</span>
                      <i class="fa fa-chevron-down text-muted ml-2 small"></i>
                    </button>
                    <div v-if="statusOpen" class="status-filter-menu">
                      <div class="status-filter-header d-flex justify-content-between align-items-center px-3 py-2">
                        <span class="small text-muted">
                          {{ filters.transaction_status.length }} selected
                        </span>
                        <a
                          href="#"
                          class="small"
                          @click.prevent="filters.transaction_status = []"
                        >
                          Clear
                        </a>
                      </div>
                      <label
                        v-for="opt in statusOptions"
                        :key="opt.value"
                        class="status-filter-item d-flex align-items-center mb-0"
                      >
                        <input
                          type="checkbox"
                          v-model="filters.transaction_status"
                          :value="opt.value"
                          class="mr-2"
                        />
                        <span>{{ opt.text }}</span>
                      </label>
                    </div>
                  </div>
                </b-col>
                <b-col md="6" lg="3" class="mb-2">
                  <label class="small text-muted mb-1">Search</label>
                  <div class="input-group">
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      placeholder="Reference or name…"
                      v-model="filters.search"
                      @keyup.enter="applyFilters"
                    />
                    <div class="input-group-append" @click="applyFilters">
                      <span class="input-group-text">
                        <i class="icon-magnifier"></i>
                      </span>
                    </div>
                  </div>
                </b-col>
              </b-form-row>
              <div class="d-flex justify-content-end mt-2">
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  class="mr-2"
                  @click="resetFilters"
                >
                  Reset
                </b-button>
                <b-button variant="primary" size="sm" @click="applyFilters">
                  Apply
                </b-button>
              </div>
            </div>

            <!-- States -->
            <div v-if="txLoading" class="tx-skel-table">
              <div class="tx-skel-head d-flex">
                <div
                  v-for="(col, i) in fields"
                  :key="`skel-h-${i}`"
                  class="tx-skel-cell"
                  :style="{ flex: skelColFlex(col.key) }"
                >
                  <div class="skel skel-line skel-sm" style="width: 60%"></div>
                </div>
              </div>
              <div
                v-for="r in 6"
                :key="`skel-row-${r}`"
                class="tx-skel-row d-flex"
              >
                <div
                  v-for="(col, i) in fields"
                  :key="`skel-c-${r}-${i}`"
                  class="tx-skel-cell"
                  :style="{ flex: skelColFlex(col.key) }"
                >
                  <div
                    v-if="col.key === 'transactionStatus'"
                    class="skel skel-pill mx-auto"
                    style="width: 80px; height: 1.2rem"
                  ></div>
                  <div
                    v-else-if="col.key === 'actions'"
                    class="skel skel-pill ml-auto"
                    style="width: 56px; height: 1.5rem"
                  ></div>
                  <div
                    v-else-if="col.key === 'sourceAmount' || col.key === 'destAmount'"
                    class="skel skel-line ml-auto"
                    :style="{ width: '70%', height: '0.95rem' }"
                  ></div>
                  <div
                    v-else
                    class="skel skel-line"
                    :style="{ width: skelLineWidth(r, i), height: '0.95rem' }"
                  ></div>
                </div>
              </div>
            </div>
            <div v-else-if="txError" class="alert alert-danger">
              <i class="fa fa-exclamation-circle mr-1"></i> {{ txError }}
              <b-button variant="outline-danger" size="sm" class="ml-3" @click="loadTransactions">
                Retry
              </b-button>
            </div>

            <template v-else>
              <b-table
                responsive
                table-class="mb-0 table-custom border"
                head-variant="light"
                :items="transactions"
                :fields="fields"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :no-local-sorting="true"
                @sort-changed="onSortChanged"
                striped
                show-empty
              >
                <template #cell(sender)="row">
                  {{ senderName(row.item) || "—" }}
                </template>

                <template #cell(receiver)="row">
                  {{ receiverName(row.item) || "—" }}
                </template>

                <template #cell(paymentMethod)="row">
                  {{ row.value || "—" }}
                </template>

                <template #cell(reference_number)="row">
                  <a
                    class="font-weight-bold text-primary cursor-pointer"
                    @click="viewTransaction(row.item)"
                  >
                    {{ row.value || row.item.id }}
                  </a>
                </template>

                <template #cell(sourceAmount)="row">
                  <span :class="sourceAmountClass(row.item)">
                    {{ sourceAmountSign(row.item) }}
                    {{ row.item.sourceCurrency }}
                    {{ formatAmount(row.value) }}
                  </span>
                </template>

                <template #cell(destAmount)="row">
                  {{ row.item.destCurrency }}
                  {{ formatAmount(row.value) }}
                </template>

                <template #cell(transactionStatus)="row">
                  <span class="badge" :class="txStatusClass(row.value)">
                    {{ row.value }}
                  </span>
                </template>

                <template #cell(transaction_date)="row">
                  {{ formatDateTime(row.value) }}
                </template>

                <template #cell(actions)="row">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="viewTransaction(row.item)"
                  >
                    <i class="fa fa-eye mr-1"></i> View
                  </b-button>
                </template>

                <template #empty>
                  <div class="text-center text-muted py-4">
                    No transactions match the current filters.
                  </div>
                </template>
              </b-table>

              <div class="d-flex py-3 align-items-center justify-content-between table-pagination">
                <div class="per-page">
                  <b-form-group label="Per page" label-for="per-page-select" class="mb-0">
                    <b-form-select
                      id="per-page-select"
                      v-model="perPage"
                      :options="pageOptions"
                      size="sm"
                      @change="onPerPageChange"
                    ></b-form-select>
                  </b-form-group>
                </div>
                <div class="number-page">
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    first-text="First"
                    last-text="Last"
                    :per-page="perPage"
                    align="right"
                    @change="onPageChange"
                  ></b-pagination>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  GET_BUSINESS_OVERVIEW,
  GET_BUSINESS_TRANSACTIONS,
  EXPORT_BUSINESS_TRANSACTIONS,
} from "@/store/actions.type";

const TRANSACTION_STATUSES = [
  "Incomplete",
  "Awaiting Funds",
  "Funds Received",
  "Processing",
  "Sent for Payment",
  "Failed",
  "Recipient Paid",
  "In Review",
  "Cancelled",
  "Refunded",
];

const STATUS_BADGE = {
  "Recipient Paid": "badge-success",
  "Funds Received": "badge-info",
  Processing: "badge-info",
  "Sent for Payment": "badge-info",
  "Awaiting Funds": "badge-warning",
  "In Review": "badge-warning",
  Incomplete: "badge-secondary",
  Failed: "badge-danger",
  Cancelled: "badge-danger",
  Refunded: "badge-dark",
};

const BUSINESS_STATUS_BADGE = {
  approved: "badge-success",
  pending: "badge-warning",
  rejected: "badge-danger",
  freezed: "badge-danger",
  suspended: "badge-danger",
  dormant: "badge-secondary",
};

export default {
  name: "BusinessFinancials",
  components: {
    "bread-crumb": () => import("@/components/BreadCrumb.vue"),
  },
  directives: {
    "click-outside": {
      bind(el, binding) {
        const handler = (e) => {
          if (!el.contains(e.target) && el !== e.target) binding.value(e);
        };
        el.__vueClickOutside__ = handler;
        document.addEventListener("click", handler);
      },
      unbind(el) {
        document.removeEventListener("click", el.__vueClickOutside__);
      },
    },
  },
  data() {
    return {
      breadCrumbIteams: [
        { title: "Business", links: "/business/list" },
        { title: "Financials", links: "" },
      ],
      business: null,
      balances: [],
      overviewLoading: false,
      overviewError: null,
      transactions: [],
      totalRows: 0,
      txLoading: false,
      txError: null,
      // null when idle; "csv" or "pdf" while that export is in flight
      exporting: null,
      statusOpen: false,
      filters: {
        date_from: "",
        date_to: "",
        transaction_status: [],
        search: "",
      },
      sortBy: "transaction_date",
      sortDesc: true,
      currentPage: 1,
      perPage: 20,
      pageOptions: [10, 20, 50, 100],
      fields: [
        { key: "transaction_date", label: "Date", sortable: false },
        { key: "sender", label: "Sender", sortable: false },
        { key: "receiver", label: "Receiver", sortable: false },
        { key: "paymentMethod", label: "Payment Method", sortable: false },
        { key: "reference_number", label: "PayAngel Ref", sortable: false },
        {
          key: "sourceAmount",
          label: "Source Amount",
          sortable: false,
          thClass: "text-right",
          tdClass: "text-right",
        },
        {
          key: "destAmount",
          label: "Dest Amount",
          sortable: false,
          thClass: "text-right",
          tdClass: "text-right",
        },
        {
          key: "transactionStatus",
          label: "Trans. Status",
          sortable: false,
          thClass: "text-center",
          tdClass: "text-center",
        },
        {
          key: "actions",
          label: "Action",
          sortable: false,
          thClass: "text-right",
          tdClass: "text-right",
        },
      ],
      statusOptions: TRANSACTION_STATUSES.map((s) => ({ value: s, text: s })),
    };
  },
  computed: {
    businessId() {
      return this.$route.params.businessid;
    },
    businessStatusLabel() {
      const s = this.business?.status || "";
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    businessStatusClass() {
      return BUSINESS_STATUS_BADGE[this.business?.status] || "badge-secondary";
    },
    statusButtonLabel() {
      const selected = this.filters.transaction_status || [];
      if (!selected.length) return "All statuses";
      if (selected.length === 1) return selected[0];
      return `${selected.length} selected`;
    },
  },
  created() {
    this.loadOverview();
    this.loadTransactions();
  },
  methods: {
    async loadOverview() {
      this.overviewLoading = true;
      this.overviewError = null;
      try {
        const response = await this.$store.dispatch(
          GET_BUSINESS_OVERVIEW,
          this.businessId,
        );
        if (response?.data?.code === "0075-000") {
          this.business = response.data.details.business || null;
          this.balances = response.data.details.balances || [];
        } else {
          this.overviewError =
            response?.data?.message || "Failed to load business overview.";
        }
      } catch (err) {
        this.overviewError =
          err?.response?.data?.message || "Failed to load business overview.";
      } finally {
        this.overviewLoading = false;
      }
    },
    buildTxPayload(extra = {}) {
      const payload = {
        businessid: this.businessId,
        page: this.currentPage,
        limit: this.perPage,
        sort: this.sortDesc ? "desc" : "asc",
        ...extra,
      };
      if (this.filters.date_from && this.filters.date_to) {
        payload.date_from = this.toApiDate(this.filters.date_from);
        payload.date_to = this.toApiDate(this.filters.date_to);
      }
      if (this.filters.transaction_status?.length) {
        payload.transaction_status = this.filters.transaction_status;
      }
      if (this.filters.search) payload.search = this.filters.search;
      return payload;
    },
    async loadTransactions() {
      this.txLoading = true;
      this.txError = null;
      try {
        const response = await this.$store.dispatch(
          GET_BUSINESS_TRANSACTIONS,
          this.buildTxPayload(),
        );
        if (response?.data?.code === "1005-000") {
          this.transactions = response.data.details || [];
          this.totalRows = response.data.query?.total || 0;
        } else {
          this.txError =
            response?.data?.message || "Failed to load transactions.";
          this.transactions = [];
          this.totalRows = 0;
        }
      } catch (err) {
        this.txError =
          err?.response?.data?.message || "Failed to load transactions.";
      } finally {
        this.txLoading = false;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.loadTransactions();
    },
    resetFilters() {
      this.filters = {
        date_from: "",
        date_to: "",
        transaction_status: [],
        search: "",
      };
      this.currentPage = 1;
      this.loadTransactions();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadTransactions();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.loadTransactions();
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.currentPage = 1;
      this.loadTransactions();
    },
    viewTransaction(item) {
      this.$router.push(
        `/business/${this.businessId}/transactions/${item.id}`,
      );
    },
    async exportTransactions(format) {
      if (this.exporting) return;
      this.exporting = format;
      try {
        const payload = this.buildTxPayload({ format });
        delete payload.page;
        delete payload.limit;
        const { blob, filename } = await this.$store.dispatch(
          EXPORT_BUSINESS_TRANSACTIONS,
          payload,
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        let message = "Export failed.";
        const data = err?.response?.data;
        if (data instanceof Blob) {
          try {
            const text = await data.text();
            const json = JSON.parse(text);
            message = json.message || message;
          } catch {
            // keep default
          }
        } else if (data?.message) {
          message = data.message;
        }
        this.$toast?.error?.(message, { position: "top-right" });
      } finally {
        this.exporting = null;
      }
    },
    txStatusClass(status) {
      return STATUS_BADGE[status] || "badge-secondary";
    },
    closeStatus() {
      this.statusOpen = false;
    },
    skelColFlex(key) {
      // rough column-width hints to mimic the real table's proportions
      const map = {
        transaction_date: 1.4,
        sender: 1.3,
        receiver: 1.3,
        paymentMethod: 1.1,
        reference_number: 1.1,
        sourceAmount: 1,
        destAmount: 1,
        transactionStatus: 1,
        actions: 0.8,
      };
      return map[key] ?? 1;
    },
    skelLineWidth(row, col) {
      // varied widths so rows don't look identical
      const widths = ["70%", "85%", "60%", "78%", "92%", "55%", "82%"];
      return widths[(row + col) % widths.length];
    },
    senderName(item) {
      const parts = [item.senderFirstName, item.senderLastName].filter(Boolean);
      if (parts.length) return parts.join(" ");
      return item.merchantSender || "";
    },
    receiverName(item) {
      const parts = [item.receiverFirstName, item.receiverLastName].filter(
        Boolean,
      );
      if (parts.length) return parts.join(" ");
      return item.merchantReceiver || "";
    },
    isOutgoing(item) {
      // Treat the row as outgoing if the business is the sender
      return (
        item.merchantSender &&
        item.merchantSender.toString() === this.businessId.toString()
      );
    },
    sourceAmountSign(item) {
      return this.isOutgoing(item) ? "-" : "+";
    },
    sourceAmountClass(item) {
      return this.isOutgoing(item) ? "text-danger" : "text-success";
    },
    toApiDate(isoDate) {
      // <input type="date"> returns YYYY-MM-DD; the API expects DD-MM-YYYY
      if (!isoDate) return "";
      const [y, m, d] = isoDate.split("-");
      return `${d}-${m}-${y}`;
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      return Number.isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
    },
    formatDateTime(value) {
      if (!value) return "—";
      const d = new Date(value);
      return Number.isNaN(d.getTime())
        ? "—"
        : d.toLocaleString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
    },
    formatAmount(value) {
      if (value == null) return "—";
      const n = Number(value);
      if (Number.isNaN(n)) return value;
      return n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.status-filter {
  position: relative;
}
.status-filter-toggle {
  width: 100%;
  text-align: left;
  cursor: pointer;
  background-color: #fff;
  color: #495057;
  font-weight: 400;
}
.status-filter-toggle:focus,
.status-filter-toggle.open {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
  outline: none;
}
.status-filter-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
}
.status-filter-header {
  border-bottom: 1px solid #eee;
  background-color: #fafafa;
}
.status-filter-item {
  padding: 0.4rem 0.85rem;
  font-size: 0.85rem;
  cursor: pointer;
  user-select: none;
}
.status-filter-item:hover {
  background-color: #f5f5f5;
}
.status-filter-item input[type="checkbox"] {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* ------- Skeleton loader ------- */
.skel {
  display: block;
  background: linear-gradient(
    90deg,
    #eef0f3 0%,
    #f7f8fa 50%,
    #eef0f3 100%
  );
  background-size: 200% 100%;
  border-radius: 4px;
  animation: skel-shimmer 1.4s ease-in-out infinite;
}
.skel-line {
  height: 0.85rem;
}
.skel-sm {
  height: 0.6rem;
  border-radius: 3px;
  opacity: 0.85;
}
.skel-pill {
  height: 1.4rem;
  border-radius: 999px;
}
@keyframes skel-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .skel {
    animation: none;
    background: #eef0f3;
  }
}

/* Transaction-table skeleton */
.tx-skel-table {
  border: 1px solid #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}
.tx-skel-head {
  background: #f6f7f9;
  border-bottom: 1px solid #e9ecef;
}
.tx-skel-row {
  border-bottom: 1px solid #f1f3f5;
}
.tx-skel-row:last-child {
  border-bottom: 0;
}
.tx-skel-row:nth-child(even) {
  background: #fafbfc;
}
.tx-skel-cell {
  padding: 0.75rem 0.85rem;
  display: flex;
  align-items: center;
  min-width: 0;
}
.tx-skel-head .tx-skel-cell {
  padding-top: 0.6rem;
  padding-bottom: 0.6rem;
}
</style>
